import {
  ActivityLogResponse,
  AsyncPreviewResponse,
  AsyncRequestOrder,
  ErrorMessageResponse,
  Order,
  OrderListResponse,
  OrderPriceResponse,
  OrderProofOfDelivery,
  OrderResolveAction,
  OrderResolveResponse,
  OrderRtoRequest,
  OrderRtoResponse,
  OrderSingleResponse,
  OrderSingleRtoResponse,
  OrdersResponse,
  OrderStatusCountResponse,
  OrderStatusTab,
  OrderTransaction,
  Product,
  ResponseListType,
  ResponseType,
  StatusAnnotation,
} from '@/types';
import { useSentryQuery, useSentryMutation } from '../utils/sentryQueryWrapper';
import type { OrderUpdatesResponse } from '@/types/orderUpdates';
import {
  addProductToOrder,
  cancelOrder,
  createOrder,
  deleteOrder,
  deleteProductFromOrder,
  fetchListByStatusSlug,
  fetchListFinished,
  fetchListInprogress,
  fetchListOnHold,
  fetchListOrderReady,
  fetchListOrders,
  fetchOrderActivityLogByOrderUID,
  fetchListReturningOrigin,
  fetchListReturningWarehouse,
  fetchOrderByUID,
  fetchOrderCollecting,
  fetchOrderEnRoute,
  fetchOrderRTO,
  fetchOrderRTOById,
  fetchOrders,
  previewOrderPrice,
  resolveActionOrder,
  rtoRequestOrder,
  updateOrder,
  updateProductByUID,
  fetchActionRequiredOrderList,
  bulkDeleteOrders,
  bulkCancelOrders,
  asyncCreateOrders,
  asyncPreviewOrderPrice,
  fetchOrderRtoRequest,
  duplicateOrder,
  fetchStatusAnnotationsByOrderUID,
  fetchOrderActiveTicket,
  fetchTransactionByOrderID,
  fetchOrderCount,
  fetchOrderProofOfDelivery,
  fetchSearchOrders,
  fetchOrderUpdates,
} from '@/api/orderService';
import { t } from 'i18next';
import useLanguageStore from '@/stores/useLanguageStore';
import { OrderStatusEnum } from '@/config';
import { ISearch } from '@/hooks/useSearchStore';
import { bulkRequestRtoOrders } from '@/api/orderService';
import { BulkActionResponse, BulkRequestRtoPayload } from '@/types/order';

// Fetch all orders
export const useOrders = () => {
  return useSentryQuery<OrdersResponse, Error>(['orders'], fetchOrders);
};

// Fetch a single order by UID
//
// BOXY-557: the order carries server-localized text (service_switch.message
// under the shipping-fee ⓘ, …) translated from the `lang` param the axios
// interceptor reads off useLanguageStore. Scoping the key by that same value
// makes a language switch a fresh fetch instead of the old locale's cache.
// invalidateQueries(['order', uid]) still prefix-matches every locale.
export const useOrderByUID = (uid: string) => {
  const language = useLanguageStore((state) => state.language);
  return useSentryQuery<OrderSingleResponse, Error>(
    ['order', uid, language],
    () => fetchOrderByUID(uid),
    { enabled: !!uid },
  );
};

// Fetch Order Proof Of Delivery
export const useOrderProofOfDelivery = (uid: string, enabled: boolean = true) => {
  return useSentryQuery<ResponseType<OrderProofOfDelivery>, Error>(
    ['order-carrier-proof', uid],
    () => fetchOrderProofOfDelivery(uid),
    {
      enabled: !!uid && enabled,
      defaultErrorMessage: '',
      retry: false,
    },
  );
};

// Fetch a single order by UID
export const useFetchOrderCount = (enabled: boolean = true) => {
  return useSentryQuery<ResponseType<OrderStatusCountResponse>, Error>(
    ['order-count'],
    () => fetchOrderCount(),
    {
      enabled,
    },
  );
};

// Fetch a Transaction by Order Id
export const useFetchTransactionByOrderID = (
  uid: string,

  lang: string,
) => {
  return useSentryQuery<ResponseListType<OrderTransaction>, Error>(
    ['order-transaction', uid, lang],
    () => fetchTransactionByOrderID(uid),
    {
      enabled: !!uid,
    },
  );
};

// Create a new order
export const useCreateOrder = () => {
  return useSentryMutation<OrdersResponse, ErrorMessageResponse, Order>(
    (order: Order) => createOrder(order),
    { defaultErrorMessage: t('failed_to_add_order') },
  );
};

export const useAsyncCreateOrders = () => {
  return useSentryMutation<OrdersResponse, ErrorMessageResponse, AsyncRequestOrder>(
    (order: AsyncRequestOrder) => asyncCreateOrders(order),
    { defaultErrorMessage: t('failed_to_add_order') },
  );
};

// Preview order price
// defaultErrorMessage: '' suppresses toast notifications — preview failures are expected
// when the form is incomplete and should fail silently (UI updates price display only).
export const usePreviewOrderPrice = () => {
  return useSentryMutation<OrderPriceResponse, ErrorMessageResponse, Order>(
    (order: Order) => previewOrderPrice(order),
    { defaultErrorMessage: '' },
  );
};

// Async Preview order price
// defaultErrorMessage: '' suppresses toast notifications — preview failures are expected
// when the form is incomplete and should fail silently (UI updates price display only).
export const useAsyncPreviewOrderPrice = () => {
  return useSentryMutation<
    ResponseType<AsyncPreviewResponse>,
    ErrorMessageResponse,
    AsyncRequestOrder
  >((order: AsyncRequestOrder) => asyncPreviewOrderPrice(order), { defaultErrorMessage: '' });
};

// Update an existing order
export const useUpdateOrder = (uid: string) => {
  return useSentryMutation<OrdersResponse, ErrorMessageResponse, Partial<Order>>(
    (orderUpdates: Partial<Order>) => updateOrder({ ...orderUpdates, uid }),
    { defaultErrorMessage: t('failed_to_edit_order') },
  );
};

// Delete an order by ID
export const useDeleteOrderById = () =>
  useSentryMutation<void, ErrorMessageResponse, string>((uid: string) => deleteOrder(uid));

// Bulk delete orders
export const useBulkDeleteOrders = () => {
  return useSentryMutation<
    void,
    ErrorMessageResponse,
    { status?: OrderStatusEnum; selectedIds?: string[] }
  >(({ status, selectedIds }: { status?: OrderStatusEnum; selectedIds?: string[] }) =>
    bulkDeleteOrders(status, selectedIds),
  );
};
// Cancel an order by UID
export const useCancelOrder = () =>
  useSentryMutation<OrdersResponse, ErrorMessageResponse, string>((uid: string) =>
    cancelOrder(uid),
  );

// Bulk delete orders
export const useBulkCancelOrders = () => {
  return useSentryMutation<
    void,
    ErrorMessageResponse,
    { status?: OrderStatusEnum; selectedIds?: string[] }
  >(({ status, selectedIds }: { status?: OrderStatusEnum; selectedIds?: string[] }) =>
    bulkCancelOrders(status, selectedIds),
  );
};
// Add a new product to an order
export const useAddProductToOrder = () => {
  return useSentryMutation<
    OrdersResponse,
    ErrorMessageResponse,
    { orderUID: string; product: Product }
  >(({ orderUID, product }: { orderUID: string; product: Product }) =>
    addProductToOrder(orderUID, product),
  );
};

// Delete a new product to an order
export const useDeleteProductFromOrder = () => {
  return useSentryMutation<
    OrdersResponse,
    ErrorMessageResponse,
    { orderUID: string; productUID: string }
  >(({ orderUID, productUID }: { orderUID: string; productUID: string }) =>
    deleteProductFromOrder(orderUID, productUID),
  );
};

// Update an existing product by its UID
export const useUpdateProductByUID = () => {
  return useSentryMutation<
    Product,
    ErrorMessageResponse,
    { orderUID: string; productUID: string; updatedProduct: Partial<Product> }
  >(
    ({
      orderUID,
      productUID,
      updatedProduct,
    }: {
      orderUID: string;
      productUID: string;
      updatedProduct: Partial<Product>;
    }) => updateProductByUID(orderUID, productUID, updatedProduct),
  );
};

// Fetch List Order ReadyToShip
export const useFetchListOrderReady = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    ['orders', page, search?.search, filterParams?.toString()],
    () => fetchListOrderReady(page, pageSize, search, filterParams),
    {
      keepPreviousData: true,
      enabled: enabled && !search?.search,
    },
  );
};

// Fetch List Order Inprogress
export const useFetchListInprogress = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`orders-${OrderStatusTab.IN_PROGRESS}`, page, search?.search, filterParams?.toString()],
    () => fetchListInprogress(page, pageSize, search, filterParams),
    {
      keepPreviousData: true,
      enabled: enabled && !search?.search,
    },
  );
};

// Fetch List Order OnHold
export const useFetchListOnHold = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`orders-${OrderStatusTab.ON_HOLD}`, page, search?.search, filterParams?.toString()],
    () => fetchListOnHold(page, pageSize, search, filterParams),
    {
      keepPreviousData: true,
      enabled: enabled && !search?.search,
    },
  );
};

// Fetch List Order Finished
export const useFetchListFinished = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`orders-${OrderStatusTab.FINISHED}`, page, search?.search, filterParams?.toString()],
    () => fetchListFinished(page, pageSize, search, filterParams),
    { keepPreviousData: true, enabled: enabled && !search?.search },
  );
};

// Fetch List Order RTO
export const useFetchListRTO = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`orders-${OrderStatusTab.RTO}`, page, search?.search, filterParams?.toString()],
    () => fetchOrderRTO(page, pageSize, search, filterParams),
    { keepPreviousData: true, enabled: enabled && !search?.search },
  );
};

// Fetch List Order RTO Request
export const useFetchListRtoRequest = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`orders-${OrderStatusTab.RTO}-requested`, page, search?.search, filterParams?.toString()],
    () => fetchOrderRtoRequest(page, pageSize, search, filterParams),
    { keepPreviousData: true, enabled: enabled && !search?.search },
  );
};

// Fetch List Order All
export const useFetchListOrders = (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    ['orders-list', page, search?.search, filterParams?.toString()],
    () => fetchListOrders(page, pageSize, search, filterParams),
    {
      keepPreviousData: true,
      enabled: (!!search?.search && !!search?.searchBy) || !!filterParams?.toString(),
    },
  );
};

// Fetch List By Status Slug
export const useFetchListByStatusSlug = (
  slug: string,
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, ErrorMessageResponse>(
    ['orders', slug, page, search?.search, filterParams?.toString()],
    () => fetchListByStatusSlug(slug, page, pageSize, search, filterParams),
    {
      keepPreviousData: true,
      enabled:
        ![
          'all',
          'action-required',
          'collecting',
          'en-route',
          'rto-in-transit',
          'rto-received-warehouse',
          'rto-requested',
        ].includes(slug) &&
        !search?.search,
    },
  );
};

// Fetch Action Required Order List
export const useFetchActionRequiredOrderList = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`order-${OrderStatusTab.ON_HOLD}`, page, search?.search, filterParams?.toString()],
    () => fetchActionRequiredOrderList(page, pageSize, search, filterParams),
    { enabled: enabled && !search?.search },
  );
};

// Resolve order action
export const useResolveActionOrder = () => {
  return useSentryMutation<
    OrderResolveResponse,
    ErrorMessageResponse,
    { orderId: string; resolveAction: OrderResolveAction }
  >(({ orderId, resolveAction }: { orderId: string; resolveAction: OrderResolveAction }) =>
    resolveActionOrder(orderId, resolveAction),
  );
};

// Rto request Order
export const useRtoRequestOrder = () => {
  return useSentryMutation<
    OrderRtoResponse,
    ErrorMessageResponse,
    { orderId: string; rtoRequest: OrderRtoRequest }
  >(({ orderId, rtoRequest }: { orderId: string; rtoRequest: OrderRtoRequest }) =>
    rtoRequestOrder(orderId, rtoRequest),
  );
};

// Fetch RTO Order By Id
export const useFetchOrderRTOById = (uid: string) => {
  return useSentryQuery<OrderSingleRtoResponse, Error>(
    ['order-rto', uid],
    () => fetchOrderRTOById(uid),
    {
      enabled: !!uid,
    },
  );
};

// Fetch order collecting
export const useFetchOrderCollecting = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`order-${OrderStatusTab.IN_PROGRESS}`, page, search?.search, filterParams?.toString()],
    () => fetchOrderCollecting(page, pageSize, search, filterParams),
    { enabled: enabled && !search?.search },
  );
};

// Fetch order en-route
export const useFetchOrderEnRoute = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`order-${OrderStatusTab.IN_PROGRESS}`, page, search?.search, filterParams?.toString()],
    () => fetchOrderEnRoute(page, pageSize, search, filterParams),
    { enabled: enabled && !search?.search },
  );
};

// Fetch order ActivityLogs - single page only
export const useFetchOrderActivityLogByOrderUID = (
  uid: string,
  lang: string,
  page: number,
  perpage = 20,
) => {
  return useSentryQuery<ActivityLogResponse, Error>(
    [`order-activity-log`, uid, lang, page, perpage],
    () => fetchOrderActivityLogByOrderUID(uid, page, perpage),
    {
      enabled: !!uid,
      keepPreviousData: true,
    },
  );
};

// Fetch List Order RTO returning-origin
export const useFetchReturningOrigin = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`order-${OrderStatusTab.RTO}-returnOrigin`, page, search?.search, filterParams?.toString()],
    () => fetchListReturningOrigin(page, pageSize, search, filterParams),
    { enabled: enabled && !search?.search },
  );
};

// Fetch List Order RTO returning-warehouse
export const useFetchReturningWarehouse = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => {
  return useSentryQuery<OrderListResponse, Error>(
    [`order-${OrderStatusTab.RTO}-returnWarehouse`, page, search?.search, filterParams?.toString()],
    () => fetchListReturningWarehouse(page, pageSize, search, filterParams),
    { enabled: enabled && !search?.search },
  );
};

// Duplicate an order by UID
export const useDuplicateOrder = () => {
  return useSentryMutation<OrderSingleResponse, ErrorMessageResponse, string>((orderId: string) =>
    duplicateOrder(orderId),
  );
};

// Fetch Order Status Annotations by UID
export const useFetchStatusAnnotationsByOrderUID = (orderId: string) => {
  return useSentryQuery<ResponseListType<StatusAnnotation>, Error>(
    ['status-annotation'],
    () => fetchStatusAnnotationsByOrderUID(orderId),
    { enabled: !!orderId },
  );
};

// Fetch active ticket for an order
export const useFetchOrderActiveTicket = (orderUid: string, enabled: boolean = true) => {
  return useSentryQuery<Awaited<ReturnType<typeof fetchOrderActiveTicket>>, Error>(
    ['order-active-ticket', orderUid],
    () => fetchOrderActiveTicket(orderUid),
    { enabled: enabled && !!orderUid },
  );
};

// Meilisearch search hook
export const useFetchSearchOrders = (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
  statusGroup?: string,
  extraFilters?: { key: string; value: string }[],
) => {
  // Map old filter_* field names to Meilisearch searchable attribute names
  const fieldMapping: Record<string, string[]> = {
    filter_phone: ['phone'],
    filter_address_text: ['address_text'],
    filter_full_name: ['full_name'],
    filter_platform_code: ['platform_code'],
    filter_pick_up_address_title: [
      'pick_up_address.address_text',
      'pick_up_address.title',
      'saved_pick_up_address.address_text',
      'saved_pick_up_address.title',
    ],
    'filter_custom_ids[]': ['custom_id'],
    filter_size: [], // handled as filter, not text search
  };

  const searchOnAttributes = search?.searchBy ? (fieldMapping[search.searchBy] || []) : undefined;
  const query = search?.search || '';
  const isPackageSize = search?.searchBy === 'filter_size';

  // Build Meilisearch filter array for non-text filters (e.g. size)
  const sizeFilters = isPackageSize && query
    ? [{ key: 'size', value: query }]
    : undefined;

  // Merge size filters + extra filters (e.g. sub-status filter)
  const mergedFilters = [...(sizeFilters ?? []), ...(extraFilters ?? [])];

  return useSentryQuery<OrderListResponse, Error>(
    ['orders-search', page, search?.search, search?.searchBy, filterParams?.toString(), statusGroup, JSON.stringify(extraFilters)],
    () =>
      fetchSearchOrders(
        page,
        pageSize,
        isPackageSize ? '' : query,
        isPackageSize ? undefined : searchOnAttributes && searchOnAttributes.length > 0 ? searchOnAttributes : undefined,
        statusGroup,
        filterParams,
        mergedFilters.length > 0 ? mergedFilters : undefined,
      ),
    {
      keepPreviousData: true,
      enabled: (!!search?.search && !!search?.searchBy) || !!filterParams?.toString() || !!statusGroup,
    },
  );
};

// BOXY-49 — lazy: the caller passes `enabled` only once the Updates tab is
// open (and the merchant's flag is on), so opening an order costs nothing.
export const orderUpdatesKey = (uid: string) => ['order-updates', uid];
export const useFetchOrderUpdates = (uid: string, enabled: boolean) =>
  useSentryQuery<OrderUpdatesResponse, ErrorMessageResponse>(
    orderUpdatesKey(uid),
    () => fetchOrderUpdates(uid),
    { enabled: !!uid && enabled, defaultErrorMessage: '', retry: false },
  );

// BOXY-376 — bulk request-RTO.
export const useBulkRequestRto = () => {
  return useSentryMutation<BulkActionResponse, ErrorMessageResponse, BulkRequestRtoPayload>(
    (payload) => bulkRequestRtoOrders(payload),
  );
};
